<template>
  <article
    :class="[
      'group relative bg-white rounded-xl border border-slate-200 overflow-hidden transition-shadow cursor-pointer',
      hoverable ? 'hover:shadow-lg' : '',
      orientation === 'horizontal' ? 'flex' : 'flex flex-col',
    ]"
    @click="onCardClick"
  >
    <!-- Image -->
    <div :class="[
      'relative bg-slate-50 overflow-hidden shrink-0',
      orientation === 'horizontal' ? 'w-32 sm:w-44 aspect-square' : 'w-full aspect-square',
    ]">
      <img
        v-if="firstImage"
        :src="firstImage"
        :alt="product.nom || product.title || $t('reviewPage.productDefault')"
        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
        decoding="async"
      />
      <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16l5-5 4 4 5-5 4 4M21 19a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14z"/></svg>
      </div>

      <!-- Discount badge -->
      <span
        v-if="reductionPct > 0"
        class="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-red-500 text-white text-xs font-semibold shadow pointer-events-none"
      >
        −{{ Math.round(reductionPct) }}%
      </span>

      <!-- Out-of-stock overlay (decorative — must not eat heart clicks) -->
      <span
        v-if="outOfStock"
        class="absolute inset-0 bg-slate-900/40 flex items-center justify-center pointer-events-none"
      >
        <span class="px-2 py-1 rounded bg-white text-slate-900 text-xs font-semibold">{{ $t('card.outOfStock') }}</span>
      </span>

      <!-- Wishlist toggle -->
      <button
        v-if="showWishlist"
        type="button"
        class="absolute top-2 right-2 h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-slate-200 flex items-center justify-center transition-all hover:scale-110 hover:bg-red-50 active:scale-95 z-10"
        :aria-label="isWishlisted ? $t('card.removeFromWishlist') : $t('card.addToWishlist')"
        :title="isWishlisted ? $t('card.removeFromWishlist') : $t('card.addToWishlist')"
        @click.stop.prevent="$emit('wishlist-toggle', product)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transition-colors"
          :class="isWishlisted ? 'text-red-500' : 'text-slate-400'"
          viewBox="0 0 24 24"
          :fill="isWishlisted ? 'currentColor' : 'none'"
          stroke="currentColor" stroke-width="2"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z"/>
        </svg>
      </button>
    </div>

    <!-- Body -->
    <div :class="[
      'p-3 sm:p-4 flex flex-col min-w-0',
      orientation === 'horizontal' ? 'flex-1' : '',
    ]">
      <h3 class="text-sm sm:text-base font-medium text-slate-900 line-clamp-2 min-h-[2.5em]">
        {{ product.nom || product.title || $t('reviewPage.productDefault') }}
      </h3>

      <!-- Brand / city sub-line -->
      <p v-if="subline" class="mt-0.5 text-xs text-slate-500 truncate">{{ subline }}</p>

      <!-- Rating -->
      <div v-if="ratingCount > 0" class="mt-1 flex items-center gap-1">
        <div class="flex">
          <svg
            v-for="n in 5" :key="n"
            class="h-3.5 w-3.5"
            :class="n <= rating ? 'text-amber-400' : 'text-slate-200'"
            fill="currentColor" viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </div>
        <span class="text-xs text-slate-500">{{ rating.toFixed(1) }}</span>
      </div>

      <!-- Price -->
      <div class="mt-2 flex items-baseline gap-2 flex-wrap">
        <template v-if="reductionPct > 0">
          <span class="text-base sm:text-lg font-semibold text-red-600">{{ formatCurrency(discountedPrice) }}</span>
          <span class="text-xs text-slate-400 line-through">{{ formatCurrency(product.prix) }}</span>
        </template>
        <template v-else>
          <span class="text-base sm:text-lg font-semibold text-slate-900">{{ formatCurrency(product.prix) }}</span>
        </template>
      </div>

      <!-- Add to cart button (optional via prop) -->
      <button
        v-if="showAddToCart"
        type="button"
        class="mt-3 h-9 w-full rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        :disabled="outOfStock"
        @click.stop="$emit('add-to-cart', product)"
      >
        {{ outOfStock ? $t('card.unavailable') : $t('card.addToCart') }}
      </button>
    </div>
  </article>
</template>

<script>
/**
 * Single product-card primitive — replaces the 6 legacy variants
 * (ProductC, Produit, allProduit, boxProduit, ProduitList, itemsProduct).
 *
 * Reads from the same `product` shape returned by /api/home/data,
 * /api/produits/edit/:id, vendor product list, and category endpoints.
 *
 * Emits:
 *   'click'           — generic click; consumer decides where to navigate
 *   'add-to-cart'     — when the (optional) add-to-cart button is clicked
 *   'wishlist-toggle' — when the (optional) wishlist heart is clicked
 */
export default {
  name: 'ProductCard',
  emits: ['click', 'add-to-cart', 'wishlist-toggle'],
  props: {
    product: { type: Object, required: true },
    orientation: { type: String, default: 'vertical', validator: (v) => ['vertical', 'horizontal'].includes(v) },
    showWishlist: { type: Boolean, default: false },
    isWishlisted: { type: Boolean, default: false },
    showAddToCart: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: true },
  },
  computed: {
    firstImage() {
      const imgs = this.product?.images;
      if (!Array.isArray(imgs) || imgs.length === 0) return null;
      // image entries may be { id, image } objects OR plain string URLs.
      const first = imgs[0];
      return typeof first === 'string' ? first : (first?.image || null);
    },
    reductionPct() {
      // Backend exposes either reduction_percentage (flat) or product.reduction.percentage.
      if (this.product.reduction_percentage != null) {
        return Number(this.product.reduction_percentage) || 0;
      }
      const r = this.product.reduction;
      return r ? Number(r.percentage) || 0 : 0;
    },
    discountedPrice() {
      if (this.product.prix_with_reduction != null) {
        return Number(this.product.prix_with_reduction) || 0;
      }
      const prix = Number(this.product.prix || 0);
      return prix - prix * (this.reductionPct / 100);
    },
    outOfStock() {
      // Use the explicit flags returned by the home / vendor endpoints,
      // otherwise compute from quantite − total_ordered.
      if (this.product.is_available === false) return true;
      if (this.product.is_in_stock === false) return true;
      const q = Number(this.product.quantite || 0);
      const o = Number(this.product.total_ordered || 0);
      const a = Number(this.product.available_quantity ?? this.product.available_stock ?? (q - o));
      return a <= 0;
    },
    rating() {
      return Number(this.product.average_rating || 0);
    },
    ratingCount() {
      return this.rating > 0 ? 1 : 0;
    },
    subline() {
      return this.product.nom_marque || this.product.ville || '';
    },
  },
  methods: {
    formatCurrency(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${n.toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
    },
    onCardClick() {
      this.$emit('click', this.product);
    },
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
</style>
